import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    CategoryInput,
    EditButtons,
} from '@barboxx/admin-components';
import { BUSINESS_CATEGORY_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessCategory } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    businessCategory: BusinessCategory;
}

export const BusinessCategoryDetailsCard = ({ businessCategory }: Props) => {
    const [editable, editableActions] = useBool(false);

    const notification = useNotifications();

    const mutateBusinessCategory = useMutation({
        mutationKey: ['businessCategory', businessCategory.id],
        mutationFn: (data: BusinessCategory.UpdateRequest) =>
            Api.BusinessSpecific.Category.putCategory(
                businessCategory.id,
                data
            ),
    });

    const formContext = useForm<BusinessCategory.UpdateRequest>({
        resolver: yupResolver(BUSINESS_CATEGORY_SCHEMA),
        defaultValues: {
            ...businessCategory,
            displayName: businessCategory?.displayName ?? '',
        },
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: BusinessCategory.UpdateRequest) => {
        mutateBusinessCategory
            .mutateAsync(values)
            .then(({ data }) => {
                notification.show('Updated business category!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notification.show('Error Updating business category!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardHeader title="Business Category Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <CategoryInput disabled={!editable} />
                        <TextFieldElement
                            label="Display Name"
                            name="displayName"
                            fullWidth
                            disabled={!editable}
                        />
                        <TextFieldElement
                            label="Display Order"
                            name="displayOrder"
                            type="number"
                            fullWidth
                            disabled={!editable}
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                        <CheckboxElement
                            label="Active"
                            name="isActive"
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Featured"
                            name="featuredCategory"
                            disabled={!editable}
                        />

                        <Can I="update" a="BusinessCategory">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
            <DevTool control={formContext.control} />
        </Card>
    );
};
